import { Building2, Check } from 'lucide-react';
import { cn } from '@/components/ui/utils';

interface BankAccountFilterCardProps {
  accountName: string;
  logoUrl?: string;
  selected: boolean;
  onToggle: () => void;
  compact?: boolean;
}

export function BankAccountFilterCard({
  accountName,
  logoUrl,
  selected,
  onToggle,
  compact = false,
}: BankAccountFilterCardProps) {
  return (
    <button
      type='button'
      onClick={onToggle}
      className={cn(
        'relative flex items-center gap-1.5 rounded-lg border transition-all',
        'hover:border-primary/50 hover:bg-accent/50',
        'focus:outline-none focus:ring-2 focus:ring-primary/20',
        compact ? 'px-2 py-1.5' : 'px-3 py-2 gap-2',
        selected
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'border-border bg-background'
      )}
    >
      {/* Bank Logo or Fallback */}
      <div
        className={cn(
          'flex-shrink-0 rounded-md overflow-hidden bg-muted flex items-center justify-center',
          compact ? 'w-6 h-6' : 'w-8 h-8'
        )}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={accountName}
            className='w-full h-full object-contain'
            onError={(e) => {
              // Fallback to icon if image fails to load
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <Building2
          className={cn(
            'text-muted-foreground',
            compact ? 'w-3 h-3' : 'w-4 h-4',
            logoUrl ? 'hidden' : ''
          )}
        />
      </div>

      {/* Account Name */}
      <span
        className={cn(
          'font-medium truncate',
          compact ? 'text-xs max-w-[100px]' : 'text-sm max-w-[120px]'
        )}
      >
        {accountName}
      </span>

      {/* Selection Indicator */}
      {selected && (
        <div
          className={cn(
            'absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 rounded-full bg-primary flex items-center justify-center',
            compact ? 'w-3.5 h-3.5' : 'w-4 h-4'
          )}
        >
          <Check
            className={cn(
              compact ? 'w-2 h-2' : 'w-2.5 h-2.5',
              'text-primary-foreground'
            )}
          />
        </div>
      )}
    </button>
  );
}

interface AllAccountsCardProps {
  selected: boolean;
  onToggle: () => void;
  compact?: boolean;
}

export function AllAccountsCard({
  selected,
  onToggle,
  compact = false,
}: AllAccountsCardProps) {
  return (
    <button
      type='button'
      onClick={onToggle}
      className={cn(
        'relative flex items-center rounded-lg border transition-all',
        'hover:border-primary/50 hover:bg-accent/50',
        'focus:outline-none focus:ring-2 focus:ring-primary/20',
        compact ? 'gap-1.5 px-2 py-1.5' : 'gap-2 px-3 py-2',
        selected
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'border-border bg-background'
      )}
    >
      <div
        className={cn(
          'flex-shrink-0 rounded-md bg-primary/10 flex items-center justify-center',
          compact ? 'w-6 h-6' : 'w-8 h-8'
        )}
      >
        <Building2
          className={cn('text-primary', compact ? 'w-3 h-3' : 'w-4 h-4')}
        />
      </div>
      <span className={cn('font-medium', compact ? 'text-xs' : 'text-sm')}>
        All Accounts
      </span>
      {selected && (
        <div
          className={cn(
            'absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 rounded-full bg-primary flex items-center justify-center',
            compact ? 'w-3.5 h-3.5' : 'w-4 h-4'
          )}
        >
          <Check
            className={cn(
              compact ? 'w-2 h-2' : 'w-2.5 h-2.5',
              'text-primary-foreground'
            )}
          />
        </div>
      )}
    </button>
  );
}
